import React from 'react';
import { useCopperOxideStore, TRIAL_PRESETS, TrialId } from '../store/useCopperOxideStore';
import {
  Scale,
  ChevronsDown,
  Wind,
  Link2,
  Droplet,
  Flame,
  Pause,
  Snowflake,
  Save,
  RotateCcw,
  Repeat,
  Sparkles,
  AlertTriangle,
  Pencil,
} from 'lucide-react';

export function CopperOxideProcedure() {
  const labStage = useCopperOxideStore(s => s.labStage);
  const currentTrial = useCopperOxideStore(s => s.currentTrial);
  const currentReadingG = useCopperOxideStore(s => s.currentReadingG);
  const tubeTempC = useCopperOxideStore(s => s.tubeTempC);
  const cuOInTubeG = useCopperOxideStore(s => s.cuOInTubeG);
  const cuInTubeG = useCopperOxideStore(s => s.cuInTubeG);
  const reductionProgress = useCopperOxideStore(s => s.reductionProgress);
  const isH2Flowing = useCopperOxideStore(s => s.isH2Flowing);
  const purgeSeconds = useCopperOxideStore(s => s.purgeSeconds);
  const h2StoppedEarlyG = useCopperOxideStore(s => s.h2StoppedEarlyG);
  const isHeating = useCopperOxideStore(s => s.isHeating);
  const results = useCopperOxideStore(s => s.results);
  const connected = useCopperOxideStore(s => s.connected);

  const isWeighing = useCopperOxideStore(s => s.isWeighing);
  const isPlacingCuO = useCopperOxideStore(s => s.isPlacingCuO);
  const isBuildingApparatus = useCopperOxideStore(s => s.isBuildingApparatus);
  const isConnecting = useCopperOxideStore(s => s.isConnecting);
  const isApplyingCottonBud = useCopperOxideStore(s => s.isApplyingCottonBud);
  const isReheating = useCopperOxideStore(s => s.isReheating);

  const selectTrial = useCopperOxideStore(s => s.selectTrial);
  const weighEmptyTube = useCopperOxideStore(s => s.weighEmptyTube);
  const addCuOToTube = useCopperOxideStore(s => s.addCuOToTube);
  const weighTubeWithCuO = useCopperOxideStore(s => s.weighTubeWithCuO);
  const buildApparatus = useCopperOxideStore(s => s.buildApparatus);
  const connectApparatus = useCopperOxideStore(s => s.connectApparatus);
  const startH2Flow = useCopperOxideStore(s => s.startH2Flow);
  const toggleH2Flow = useCopperOxideStore(s => s.toggleH2Flow);
  const toggleHeating = useCopperOxideStore(s => s.toggleHeating);
  const applyCottonBud = useCopperOxideStore(s => s.applyCottonBud);
  const weighFinal = useCopperOxideStore(s => s.weighFinal);
  const checkConstantMass = useCopperOxideStore(s => s.checkConstantMass);
  const recordObservation = useCopperOxideStore(s => s.recordObservation);
  const resetTrial = useCopperOxideStore(s => s.resetTrial);

  const preset = TRIAL_PRESETS[currentTrial];

  const animating =
    isWeighing || isPlacingCuO || isBuildingApparatus || isConnecting ||
    isApplyingCottonBud || isReheating;

  const purgeComplete = purgeSeconds >= 10;
  const canApplyCottonBud = labStage === 'cooling' && tubeTempC < 35;
  const reductionDone = reductionProgress >= 0.99;

  return (
    <div className="flex flex-col h-full pointer-events-auto">
      <div className="pb-4 mb-4 border-b border-border/50">
        <h2 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-rose-600 bg-clip-text text-transparent flex items-center gap-2">
          Empirical Formula of CuO
        </h2>
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Trial {currentTrial}/3 — {preset.cuoMassG.toFixed(2)} g CuO
        </p>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Select Trial</h3>
          <div className="grid grid-cols-3 gap-2">
            {([1, 2, 3] as TrialId[]).map(id => {
              const done = results.some(r => r.trialId === id);
              const active = currentTrial === id;
              return (
                <button
                  key={id}
                  onClick={() => selectTrial(id)}
                  className={`py-2 rounded-lg text-sm font-semibold border transition-all ${
                    active ? 'bg-rose-500 text-white border-rose-500' :
                    done ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' :
                    'bg-card text-foreground border-border hover:border-rose-500/40'
                  }`}
                >
                  {TRIAL_PRESETS[id].cuoMassG.toFixed(1)} g{done ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4 p-3 rounded-lg bg-card border border-border space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Stage</span><span className="font-semibold capitalize">{labStage.replace(/-/g, ' ')}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Balance</span><span className="font-mono">{currentReadingG.toFixed(2)} g</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Tube temp</span><span className="font-mono">{tubeTempC.toFixed(0)} °C</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">CuO remaining</span><span className="font-mono text-slate-700 dark:text-slate-200">{cuOInTubeG.toFixed(3)} g</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Cu formed</span><span className="font-mono text-orange-500">{cuInTubeG.toFixed(3)} g</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">H₂ flow</span><span className={`font-mono ${isH2Flowing ? 'text-emerald-500' : 'text-muted-foreground'}`}>{isH2Flowing ? 'flowing' : 'off'}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Purge time</span><span className={`font-mono ${purgeComplete ? 'text-emerald-500' : 'text-amber-500'}`}>{purgeSeconds.toFixed(1)} / 10 s</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Re-oxidised</span><span className={`font-mono ${h2StoppedEarlyG > 0.01 ? 'text-red-500' : 'text-muted-foreground'}`}>{h2StoppedEarlyG.toFixed(3)} g</span></div>
        </div>

        {labStage === 'purging' && !purgeComplete && (
          <div className="mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Wait for H₂ to flow for <strong>10 seconds</strong> before lighting the lamp. Heating early can cause a back-flash.</span>
          </div>
        )}

        <div className="space-y-3">
          <button
            disabled={labStage !== 'setup' || animating}
            onClick={weighEmptyTube}
            className="w-full py-3 rounded-xl bg-slate-500 hover:bg-slate-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-slate-500/20"
          >
            <Scale className="w-4 h-4" /> {isWeighing && labStage === 'setup' ? 'Weighing on the balance…' : '1. Weigh Empty Glass Tube'}
          </button>

          <button
            disabled={labStage !== 'tube-weighed' || animating}
            onClick={addCuOToTube}
            className="w-full py-3 rounded-xl bg-zinc-700 hover:bg-zinc-800 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-zinc-700/20"
          >
            <ChevronsDown className="w-4 h-4" /> {isPlacingCuO ? 'Placing CuO…' : '2. Add CuO Powder to Tube'}
          </button>

          <button
            disabled={labStage !== 'cuo-added' || animating}
            onClick={weighTubeWithCuO}
            className="w-full py-3 rounded-xl bg-slate-500 hover:bg-slate-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-slate-500/20"
          >
            <Scale className="w-4 h-4" /> {isWeighing && labStage === 'cuo-added' ? 'Weighing on the balance…' : '3. Weigh Tube + CuO'}
          </button>

          <button
            disabled={labStage !== 'cuo-weighed' || animating}
            onClick={buildApparatus}
            className="w-full py-3 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-indigo-500/20"
          >
            <Droplet className="w-4 h-4" /> {isBuildingApparatus ? 'Setting up apparatus…' : '4. Set Up Gas Generator & Safety Bottle'}
          </button>

          <button
            disabled={labStage !== 'apparatus-built' || animating}
            onClick={connectApparatus}
            className="w-full py-3 rounded-xl bg-violet-500 hover:bg-violet-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-violet-500/20"
          >
            <Link2 className="w-4 h-4" /> {isConnecting ? 'Connecting tubing…' : '5. Connect Apparatus (rubber tubing)'}
          </button>

          {labStage === 'connected' && (
            <button
              disabled={animating}
              onClick={startH2Flow}
              className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-cyan-500/20"
            >
              <Wind className="w-4 h-4" /> 6. Start H₂ Flow (10 s purge)
            </button>
          )}

          {(labStage === 'purging' || labStage === 'heating' || labStage === 'reducing') && (
            <button
              disabled={!purgeComplete && !isHeating}
              onClick={toggleHeating}
              className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                isHeating
                  ? 'bg-red-500/10 text-red-500 border border-red-500/50'
                  : purgeComplete
                    ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/20'
                    : 'bg-muted text-muted-foreground border border-border'
              }`}
            >
              {isHeating ? <><Pause className="w-4 h-4" /> 8. Stop Heating</> : <><Flame className="w-4 h-4" /> 7. Light Spirit Lamp</>}
            </button>
          )}

          {labStage === 'reducing' && reductionDone && isHeating && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-700 dark:text-emerald-300">
              ✓ All CuO has turned copper-brown. Stop heating now.
            </div>
          )}

          {labStage === 'cooling' && (
            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-sm text-cyan-700 dark:text-cyan-300 flex items-start gap-2">
              <Snowflake className="w-4 h-4 mt-0.5 shrink-0" />
              <span>9. Keep H₂ flowing while the tube cools to room temperature ({tubeTempC.toFixed(0)} °C). Stopping H₂ early lets hot Cu re-oxidise.</span>
            </div>
          )}

          {(labStage === 'cooling' || labStage === 'dried' || labStage === 'final-weighed' || labStage === 'constant-mass') && (
            <button
              disabled={!canApplyCottonBud || animating || labStage !== 'cooling'}
              onClick={applyCottonBud}
              className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-sky-500/20"
            >
              <Droplet className="w-4 h-4" /> {
                labStage !== 'cooling' ? '10. ✓ Cotton bud applied'
                : isApplyingCottonBud ? 'Drying with cotton bud…'
                : canApplyCottonBud ? '10. Apply Cotton Bud (dry the tube)'
                : `Wait for tube to cool… ${tubeTempC.toFixed(0)}°C`
              }
            </button>
          )}

          {(labStage === 'dried' || labStage === 'final-weighed' || labStage === 'constant-mass') && (
            <button
              disabled={labStage !== 'dried' || animating}
              onClick={weighFinal}
              className="w-full py-3 rounded-xl bg-slate-500 hover:bg-slate-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-slate-500/20"
            >
              <Scale className="w-4 h-4" /> {
                labStage !== 'dried' ? '11. ✓ Final mass recorded'
                : isWeighing ? 'Weighing on the balance…'
                : '11. Weigh Final Mass'
              }
            </button>
          )}

          {labStage === 'final-weighed' && (
            <button
              disabled={animating}
              onClick={checkConstantMass}
              className="w-full py-3 rounded-xl bg-purple-500 hover:bg-purple-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-purple-500/20"
            >
              <Repeat className="w-4 h-4" /> {isReheating ? 'Reheating to check…' : '12. Reheat & Confirm Constant Mass'}
            </button>
          )}

          {labStage === 'constant-mass' && (
            <button
              disabled={animating}
              onClick={recordObservation}
              className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-500/20"
            >
              <Save className="w-4 h-4" /> 13. Record & Calculate Empirical Formula
            </button>
          )}

          {(labStage === 'observed' || labStage === 'completed') && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-700 dark:text-emerald-300">
              <p className="font-bold flex items-center gap-2"><Pencil className="w-4 h-4"/> Trial {currentTrial} recorded.</p>
              <p className="mt-1 text-xs">Open the <em>Data</em> tab to see the mole ratio, or pick another trial above.</p>
            </div>
          )}

          {/* Secondary toggle: H₂ flow on/off — important for the penalty mechanic */}
          {connected && (
            <button
              onClick={toggleH2Flow}
              className={`w-full py-2 rounded-xl text-sm flex items-center justify-center gap-2 font-medium border ${
                isH2Flowing
                  ? 'bg-card text-cyan-600 dark:text-cyan-300 border-cyan-500/40 hover:bg-muted/60'
                  : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
              }`}
            >
              <Wind className="w-3 h-3" /> {isH2Flowing ? 'Stop H₂ Flow' : 'Resume H₂ Flow'}
              {!isH2Flowing && tubeTempC > 80 && (
                <span className="text-red-500 text-xs ml-1">(re-oxidising!)</span>
              )}
            </button>
          )}

          <button
            onClick={resetTrial}
            className="w-full py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/60 text-sm flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-3 h-3" /> Reset Trial
          </button>
        </div>

        <div className="mt-6 bg-rose-500/10 border border-rose-500/30 rounded-lg p-4">
          <h4 className="text-rose-600 dark:text-rose-400 text-xs font-bold uppercase tracking-wider mb-2">Tips</h4>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside ml-2">
            <li>The black CuO turns reddish-brown as it is reduced to Cu metal.</li>
            <li>Hydrogen gas must flow ≥10 s before lighting the lamp.</li>
            <li>Keep H₂ flowing while the tube cools — otherwise Cu re-oxidises.</li>
            <li>The cotton bud removes condensed water from the tube before final weighing.</li>
            <li>Expected ratio Cu:O = <span className="font-mono font-bold">1 : 1</span> → empirical formula <span className="font-mono font-bold">CuO</span>.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default CopperOxideProcedure;
